/**
 * Noms de pays et de langues en français, et tris qui respectent les accents.
 *
 * DataForSEO rend ses libellés en anglais — « French », « Belgium », « Germany » —, ce qui
 * laissait des menus déroulants anglais au milieu d'une interface française. Plutôt que de
 * maintenir deux tables de traduction, on s'appuie sur `Intl.DisplayNames`, présent dans tous
 * les navigateurs visés : il traduit un code ISO en toutes lettres, et suit la locale.
 *
 * Toutes les fonctions retombent sur la valeur d'origine en cas de code inconnu : un pays mal
 * traduit reste préférable à un menu vide.
 */

const creerTraducteur = (type) => {
  try {
    return new Intl.DisplayNames(['fr'], { type });
  } catch (e) {
    // Environnement sans support d'Intl.DisplayNames : on rendra les libellés d'origine
    return null;
  }
};

const traducteurPays = creerTraducteur('region');
const traducteurLangues = creerTraducteur('language');

/** Première lettre en capitale — `Intl` rend les langues en minuscules (« français »). */
const capitaliser = (texte) => (texte ? texte.charAt(0).toUpperCase() + texte.slice(1) : texte);

/**
 * Nom français d'un pays, depuis son code ISO 3166-1 alpha-2.
 * @param {string} codeIso - « FR », « BE »…
 * @param {string} secours - libellé à rendre si la traduction échoue
 */
export const nomDePays = (codeIso, secours = '') => {
  if (!codeIso || !traducteurPays) return secours;
  try {
    const traduit = traducteurPays.of(String(codeIso).toUpperCase());
    // `of()` rend le code inchangé quand il ne le connaît pas
    return traduit && traduit !== String(codeIso).toUpperCase() ? traduit : secours;
  } catch (e) {
    return secours;
  }
};

/**
 * Nom français d'une langue, depuis son code ISO 639.
 * @param {string} code - « fr », « en », « pt-br »…
 * @param {string} secours - libellé à rendre si la traduction échoue
 */
export const nomDeLangue = (code, secours = '') => {
  if (!code || !traducteurLangues) return secours;
  try {
    const traduit = traducteurLangues.of(String(code).toLowerCase());
    return traduit && traduit !== String(code).toLowerCase() ? capitaliser(traduit) : secours;
  } catch (e) {
    return secours;
  }
};

/**
 * Tri alphabétique français.
 *
 * `localeCompare` et non une comparaison brute : sans lui, « Élan » passe après « Zèbre », et
 * les clients dont le nom commence par une majuscule accentuée se retrouvent en fin de liste.
 *
 * @param {Array} liste
 * @param {Function} cle - extrait le texte à comparer (par défaut, `element.name`)
 */
export const trierParNom = (liste, cle = (element) => element?.name) =>
  [...(liste || [])].sort((a, b) =>
    String(cle(a) || '').localeCompare(String(cle(b) || ''), 'fr', { sensitivity: 'base' })
  );
